import React from 'react';
import { Button, DotLoading, Input, NavBar, PullToRefresh } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { manualActivityStyles } from './styles';
import { ManualActivityState } from './types';
import { H5RedemptionAuthMethod } from '../../../api/h5-manual-activity';
import { getRedemptionCredentialDisplayError } from './model';

interface ManualActivityViewProps {
  state: ManualActivityState;
  onRefresh: () => Promise<void>;
  onNextTask: () => Promise<void>;
  onCopyPhone: () => Promise<void>;
  onCopyMessage: () => Promise<void>;
  onMarkSent: () => Promise<void>;
  onStartRedemption: () => Promise<void>;
  onRedemptionPhoneChange: (phone: string) => void;
  onRedemptionAuthMethodChange: (authMethod: H5RedemptionAuthMethod) => void;
  onRegenerateRedemptionCredential: () => Promise<void>;
}

export const ManualActivityView: React.FC<ManualActivityViewProps> = ({
  state,
  onRefresh,
  onNextTask,
  onCopyPhone,
  onCopyMessage,
  onMarkSent,
  onStartRedemption,
  onRedemptionPhoneChange,
  onRedemptionAuthMethodChange,
  onRegenerateRedemptionCredential,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('manualActivity');
  const overview = state.overview;
  const task = state.task;
  const pendingCount = overview?.pending_count || 0;
  const threshold = overview?.redeem_threshold || 10;
  const progress = Math.min(100, Math.round((pendingCount / Math.max(threshold, 1)) * 100));
  const redemptionNeedsLoginStart = Boolean(
    state.redemption
      && !state.redemption.wa_account_id
      && (
        (
          state.redemption.status === 'waiting_login'
          && !state.redemption.wa_connection_attempt_id
          && !state.redemption.wa_account_id
        )
        || ['failed', 'expired'].includes(state.redemption.status)
      )
  );
  const redemptionCanRegenerateCredential = Boolean(
    state.redemption
      && ['waiting_login', 'failed', 'expired'].includes(state.redemption.status)
      && !state.redemption.wa_account_id
  );
  const redemptionCanRetryAccountScan = Boolean(
    state.redemption
      && state.redemption.wa_account_id
      && (
        ['failed', 'expired'].includes(state.redemption.status)
        || (state.redemption.status === 'scanning' && !state.redemption.wa_connection_attempt_id)
      )
  );
  const hasQrPayload = Boolean(state.redemption?.qr_payload);
  const hasPairingCode = Boolean(state.redemption?.pairing_code);
  const activeAuthMethod: H5RedemptionAuthMethod = hasQrPayload && !hasPairingCode
    ? 'qr'
    : hasPairingCode && !hasQrPayload
      ? 'pairing_code'
      : state.redemption?.auth_method || state.redemptionAuthMethod;
  const canSwitchRedemptionAuth = Boolean(
    state.redemption
      && !state.redemption.wa_account_id
      && ['waiting_login', 'failed', 'expired'].includes(state.redemption.status)
  );
  const canRegenerateCredential = Boolean(
    state.redemption
      && !state.redemption.wa_account_id
      && ['waiting_login', 'failed', 'expired'].includes(state.redemption.status)
  );
  const hasVisibleCredential = activeAuthMethod === 'qr'
    ? Boolean(state.redemptionQrImage)
    : Boolean(state.redemption?.pairing_code);
  const activeAuthMethodLabel = t(activeAuthMethod === 'qr' ? 'authQr' : 'authPairingCode');
  const redemptionDisplayError = getRedemptionCredentialDisplayError(state.redemption?.error_message, t);
  const credentialFailed = Boolean(
    state.redemption
      && (
        ['failed', 'expired'].includes(state.redemption.status)
        || (
          state.redemption.status === 'waiting_login'
          && !state.redemption.wa_connection_attempt_id
          && !hasVisibleCredential
          && Boolean(state.redemption.error_message)
        )
      )
      && !hasVisibleCredential
  );
  const credentialLoading = Boolean(
    state.redemption
      && !credentialFailed
      && ['waiting_login', 'scanning'].includes(state.redemption.status)
      && !hasVisibleCredential
      && (state.credentialLoading || state.redemption.wa_connection_attempt_id || hasQrPayload || hasPairingCode)
  );
  const redemptionTotal = state.redemption
    ? state.redemption.pending_count + state.redemption.verified_count + state.redemption.rejected_count
    : 0;
  const redemptionProcessed = state.redemption
    ? state.redemption.verified_count + state.redemption.rejected_count
    : 0;
  const matchProgress = redemptionTotal > 0
    ? Math.round((redemptionProcessed / redemptionTotal) * 100)
    : 0;
  const redemptionProgress = (() => {
    if (!state.redemption) {
      return { percent: 0, label: '' };
    }
    if (state.redemption.status === 'completed') {
      return { percent: 100, label: t('scanProgress.completed') };
    }
    if (['failed', 'expired', 'destroyed'].includes(state.redemption.status)) {
      return { percent: matchProgress, label: t('scanProgress.failed') };
    }
    if (state.redemption.status === 'waiting_login') {
      if (credentialFailed) {
        return { percent: 10, label: t('scanProgress.failed') };
      }
      if (hasVisibleCredential) {
        return { percent: 25, label: t('scanProgress.waitingUserLogin') };
      }
      return { percent: 10, label: t('scanProgress.generatingCredential') };
    }
    if (state.redemption.status === 'logged_in') {
      return { percent: 45, label: t('scanProgress.loginReady') };
    }
    if (state.redemption.status === 'scanning') {
      if (hasVisibleCredential) {
        return { percent: 25, label: t('scanProgress.waitingUserLogin') };
      }
      if (state.redemption.wa_connection_attempt_id && redemptionProcessed === 0) {
        return { percent: 45, label: t('scanProgress.restoringRuntime') };
      }
      const scanPercent = redemptionTotal > 0
        ? Math.min(99, Math.max(55, Math.round(55 + matchProgress * 0.44)))
        : 55;
      return { percent: scanPercent, label: t('scanProgress.matchingTasks', { done: redemptionProcessed, total: redemptionTotal }) };
    }
    return { percent: 5, label: t('scanProgress.preparing') };
  })();
  const shouldShowRedemptionLoginPanel = Boolean(
    state.redemption
      && (
        ['created', 'waiting_login', 'failed', 'expired'].includes(state.redemption.status)
        || hasQrPayload
        || hasPairingCode
      )
  );
  const shouldShowRedemptionCard = Boolean(
    state.redemption
      && !['completed', 'destroyed'].includes(state.redemption.status)
  );

  if (state.loading) {
    return (
      <div className="webview-page" style={{ ...manualActivityStyles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <DotLoading />
      </div>
    );
  }

  return (
    <div className="webview-page" style={manualActivityStyles.page}>
      <NavBar back={null}>{t('navTitle')}</NavBar>
      <PullToRefresh onRefresh={onRefresh} headHeight={0}>
        <div className="webview-section">
          <div style={manualActivityStyles.hero}>
            <div style={manualActivityStyles.eyebrow}>
              <span style={manualActivityStyles.eyebrowBar} />
              <span>{t('eyebrow')}</span>
            </div>
            <div style={manualActivityStyles.title}>{t('title')}</div>
            <div style={manualActivityStyles.subtitle}>
              {t('subtitle', { threshold })}
            </div>

            <div style={manualActivityStyles.statGrid}>
              <StatBox label={t('todayPoints')} value={overview?.today_confirmed_points || 0} />
              <StatBox label={t('pending')} value={pendingCount} />
              <StatBox label={t('threshold')} value={threshold} />
            </div>
            <div style={manualActivityStyles.progressTrack}>
              <div style={{ ...manualActivityStyles.progressFill, width: `${progress}%` }} />
            </div>
          </div>
        </div>

        {state.redeemRequired ? (
          <div className="webview-section">
            <div style={manualActivityStyles.notice}>
              {t('redeemNotice', { threshold })}
            </div>
          </div>
        ) : null}

        {state.error ? (
          <div className="webview-section">
            <div style={manualActivityStyles.notice}>{state.error}</div>
          </div>
        ) : null}

        {shouldShowRedemptionCard && state.redemption ? (
          <div className="webview-section">
            <div style={manualActivityStyles.redemptionCard}>
              <div style={manualActivityStyles.taskHeader}>
                <div>
                  <div style={manualActivityStyles.taskTitle}>{t('redemptionBatch')}</div>
                  <div style={manualActivityStyles.redemptionId}>{state.redemption.id}</div>
                </div>
                <div style={manualActivityStyles.pill}>{t(`status.${state.redemption.status}`)}</div>
              </div>
              <div style={manualActivityStyles.redemptionStats}>
                <StatBox label={t('redemptionPending')} value={state.redemption.pending_count} />
                <StatBox label={t('redemptionVerified')} value={state.redemption.verified_count} />
                <StatBox label={t('redemptionRejected')} value={state.redemption.rejected_count} />
              </div>
              <div style={manualActivityStyles.scanProgressBox}>
                <div style={manualActivityStyles.scanProgressHeader}>
                  <span>{redemptionProgress.label}</span>
                  <span>{redemptionProgress.percent}%</span>
                </div>
                <div style={manualActivityStyles.scanProgressTrack}>
                  <div style={{ ...manualActivityStyles.scanProgressFill, width: `${redemptionProgress.percent}%` }} />
                </div>
                <div style={manualActivityStyles.scanProgressMeta}>
                  {t('scanProgress.checkedCount', { done: redemptionProcessed, total: redemptionTotal })}
                </div>
              </div>
              {shouldShowRedemptionLoginPanel ? (
                <>
                  <div style={manualActivityStyles.redemptionAuthToolbar}>
                    <div style={manualActivityStyles.authToolbarHeader}>
                      <div style={manualActivityStyles.fieldLabel}>{t('authMethod')}</div>
                      {canRegenerateCredential ? (
                        <button
                          type="button"
                          disabled={state.actionLoading}
                          style={{
                            ...manualActivityStyles.refreshCredentialButton,
                            ...(state.actionLoading ? manualActivityStyles.refreshCredentialButtonDisabled : {}),
                          }}
                          onClick={() => void onRegenerateRedemptionCredential()}
                        >
                          {t('regenerateCurrentCredential', { method: activeAuthMethodLabel })}
                        </button>
                      ) : null}
                    </div>
                    <div style={manualActivityStyles.authSwitch}>
                      <button
                        type="button"
                        disabled={!canSwitchRedemptionAuth || state.actionLoading}
                        style={{
                          ...manualActivityStyles.authSwitchButton,
                          ...(activeAuthMethod === 'pairing_code' ? manualActivityStyles.authSwitchButtonActive : {}),
                          ...(!canSwitchRedemptionAuth || state.actionLoading ? manualActivityStyles.authSwitchButtonDisabled : {}),
                        }}
                        onClick={() => onRedemptionAuthMethodChange('pairing_code')}
                      >
                        {t('authPairingCode')}
                      </button>
                      <button
                        type="button"
                        disabled={!canSwitchRedemptionAuth || state.actionLoading}
                        style={{
                          ...manualActivityStyles.authSwitchButton,
                          ...(activeAuthMethod === 'qr' ? manualActivityStyles.authSwitchButtonActive : {}),
                          ...(!canSwitchRedemptionAuth || state.actionLoading ? manualActivityStyles.authSwitchButtonDisabled : {}),
                        }}
                        onClick={() => onRedemptionAuthMethodChange('qr')}
                      >
                        {t('authQr')}
                      </button>
                    </div>
                  </div>
                  <div style={manualActivityStyles.qrPlaceholder}>
                    {activeAuthMethod === 'pairing_code' && state.redemption.pairing_code ? (
                      <div>
                        <div style={manualActivityStyles.pairingCode}>{state.redemption.pairing_code}</div>
                        <div style={manualActivityStyles.placeholderHint}>{t('redemptionPairingHint')}</div>
                      </div>
                    ) : activeAuthMethod === 'qr' && state.redemptionQrImage ? (
                      <div>
                        <img alt="WhatsApp QR" src={state.redemptionQrImage} style={manualActivityStyles.qrImage} />
                        <div style={manualActivityStyles.placeholderHint}>{t('redemptionCredentialReady')}</div>
                      </div>
                    ) : credentialFailed ? (
                      <div style={manualActivityStyles.credentialFailure}>
                        <div style={manualActivityStyles.credentialFailureTitle}>
                          {t(state.redemption.status === 'expired' ? 'redemptionCredentialExpired' : 'redemptionCredentialFailed', {
                            method: t(activeAuthMethod === 'qr' ? 'authQr' : 'authPairingCode'),
                          })}
                        </div>
                        {redemptionDisplayError ? (
                          <div style={manualActivityStyles.placeholderHint}>{redemptionDisplayError}</div>
                        ) : null}
                        <div style={manualActivityStyles.placeholderHint}>{t('redemptionCredentialRetryHint')}</div>
                      </div>
                    ) : credentialLoading ? (
                      <div style={manualActivityStyles.credentialLoading}>
                        <DotLoading color="primary" />
                        <div style={manualActivityStyles.placeholderHint}>
                          {t('redemptionCredentialLoading', {
                            method: t(activeAuthMethod === 'qr' ? 'authQr' : 'authPairingCode'),
                          })}
                        </div>
                      </div>
                    ) : (
                      <div style={manualActivityStyles.placeholderHint}>{t('redemptionWaitingHint')}</div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="webview-section">
          {task ? (
            <div style={manualActivityStyles.taskCard}>
              <div style={manualActivityStyles.taskHeader}>
                <div>
                  <div style={manualActivityStyles.taskTitle}>{task.campaign_name || t('activityTask')}</div>
                  <div style={{ marginTop: 5, color: '#858da1', fontSize: 12, fontWeight: 700 }}>
                    {t('rewardLine', { points: task.points, nonce: task.message_nonce })}
                  </div>
                </div>
                <div style={manualActivityStyles.pill}>{task.status.replace('_', ' ')}</div>
              </div>

              <div style={manualActivityStyles.field}>
                <div style={manualActivityStyles.fieldLabel}>{t('targetPhone')}</div>
                <div style={manualActivityStyles.phoneValue}>{task.target_phone}</div>
              </div>

              <div style={manualActivityStyles.field}>
                <div style={manualActivityStyles.fieldLabel}>{t('message')}</div>
                <div style={manualActivityStyles.messageValue}>{task.message_text}</div>
              </div>

              <div style={manualActivityStyles.actions}>
                <Button block fill="outline" style={manualActivityStyles.secondaryButton} onClick={onCopyPhone}>
                  {t('copyPhone')}
                </Button>
                <Button block fill="outline" style={manualActivityStyles.secondaryButton} onClick={onCopyMessage}>
                  {t('copyMessage')}
                </Button>
                <Button block color="primary" loading={state.actionLoading} style={manualActivityStyles.primaryButton} onClick={onMarkSent}>
                  {t('sentNext')}
                </Button>
              </div>
            </div>
          ) : (
            <div style={manualActivityStyles.emptyCard}>
              <div style={{ fontSize: 18, lineHeight: 1.25, fontWeight: 800, color: '#1f2430' }}>
                {state.redeemRequired ? t('redemptionRequired') : t('readyTitle')}
              </div>
              <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.55, color: '#858da1', fontWeight: 600 }}>
                {state.redeemRequired
                  ? t('redeemRequiredHint')
                  : t('readyHint')}
              </div>
              <div style={manualActivityStyles.actions}>
                {state.redeemRequired ? (
                  <>
                    {(!state.redemption || redemptionNeedsLoginStart) ? (
                      <div style={manualActivityStyles.redemptionForm}>
                        <div style={manualActivityStyles.fieldLabel}>{t('whatsappPhone')}</div>
                        <div style={manualActivityStyles.inputShell}>
                          <Input
                            clearable
                            value={state.redemptionPhone}
                            placeholder={t('phonePlaceholder')}
                            onChange={onRedemptionPhoneChange}
                          />
                        </div>
                        <div style={manualActivityStyles.authSwitch}>
                          <button
                            type="button"
                            style={{
                              ...manualActivityStyles.authSwitchButton,
                              ...(state.redemptionAuthMethod === 'pairing_code' ? manualActivityStyles.authSwitchButtonActive : {}),
                            }}
                            onClick={() => onRedemptionAuthMethodChange('pairing_code')}
                          >
                            {t('authPairingCode')}
                          </button>
                          <button
                            type="button"
                            style={{
                              ...manualActivityStyles.authSwitchButton,
                              ...(state.redemptionAuthMethod === 'qr' ? manualActivityStyles.authSwitchButtonActive : {}),
                            }}
                            onClick={() => onRedemptionAuthMethodChange('qr')}
                          >
                            {t('authQr')}
                          </button>
                        </div>
                      </div>
                    ) : null}
                    <Button
                      block
                      color="primary"
                      loading={state.actionLoading}
                      style={manualActivityStyles.primaryButton}
                      onClick={onStartRedemption}
                    >
                      {state.redemption
                        ? (redemptionCanRegenerateCredential || redemptionCanRetryAccountScan ? t('regenerateCredential') : t('refreshRedemption'))
                        : t('startRedemption')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      block
                      color="primary"
                      loading={state.actionLoading}
                      style={manualActivityStyles.primaryButton}
                      onClick={onNextTask}
                    >
                      {t('claimTask')}
                    </Button>
                    <Button block fill="outline" style={manualActivityStyles.secondaryButton} onClick={() => navigate('/campaigns')}>
                      {t('viewCampaigns')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </PullToRefresh>
    </div>
  );
};

const StatBox: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div style={manualActivityStyles.statBox}>
    <div style={manualActivityStyles.statLabel}>{label}</div>
    <div style={manualActivityStyles.statValue}>{value.toLocaleString()}</div>
  </div>
);
